import html2Canvas from 'html2canvas'
import JsPDF from 'jspdf'

const PAGE_WIDTH = 841.89 // A4 横向宽 pt
const PAGE_HEIGHT = 595.28 // A4 横向高 pt
const PAGE_MARGIN = 10 // 页边距 pt
const MAX_CANVAS_PIXELS = 16000000 // Safari canvas 最大像素预算

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// 截图倍率自适应：超出像素预算逐步降级，下限 1
function pickScale(width, height) {
  let scale = 2
  while (scale > 1 && width * height * scale * scale > MAX_CANVAS_PIXELS) {
    scale -= 0.25
  }
  return scale
}

// 离屏宿主：规避原容器 overflow-x 裁剪，截图过程不扰动页面布局
// 外层 0 尺寸 overflow:hidden 裁剪容器负责屏幕上完全隐藏（否则全局 loading 半透明期间
// 宿主白底内容会透过前景显示成重影）；html2canvas 以宿主为渲染根，不受祖先 overflow 裁剪
function buildHost({ tableEl, legendEls, title }) {
  const wrapper = document.createElement('div')
  wrapper.style.cssText = 'position:absolute;left:0;top:0;width:0;height:0;overflow:hidden;'

  const host = document.createElement('div')
  host.style.cssText = 'position:absolute;left:0;top:0;background:#fff;width:max-content;'

  const titleEl = document.createElement('div')
  titleEl.style.cssText = 'font-size:18px;font-weight:600;color:#252733;padding:12px 0;'
  titleEl.textContent = title
  host.appendChild(titleEl)

  const legendRow = document.createElement('div')
  legendRow.style.cssText = 'display:flex;flex-wrap:wrap;padding-bottom:12px;'
  legendEls.forEach((el) => legendRow.appendChild(el.cloneNode(true)))
  host.appendChild(legendRow)

  const tableClone = tableEl.cloneNode(true)
  host.appendChild(tableClone)
  wrapper.appendChild(host)
  document.body.appendChild(wrapper)
  return { wrapper, host, titleEl, legendRow, tableClone }
}

// 块相对宿主的 CSS 像素位置（rect 差值，免疫 offsetParent 与 margin 折叠）
function rectInHost(host, el) {
  const { left, top } = host.getBoundingClientRect()
  const rect = el.getBoundingClientRect()
  return { x: rect.left - left, y: rect.top - top, w: rect.width, h: rect.height }
}

/**
 * 评估得分表导出横向 A4 PDF：按主领域块（ant-row-flex）分页，换页不切断领域块
 * @param {HTMLElement} tableEl 得分表根 DOM
 * @param {HTMLElement[]} legendEls 图例节点（达标对勾 + 评估轮次色块）
 * @param {String} title PDF 首页标题文本
 * @param {String} fileName 下载文件名
 */
export async function exportScoreTablePdf({ tableEl, legendEls, title, fileName }) {
  const { wrapper, host, titleEl, legendRow, tableClone } = buildHost({ tableEl, legendEls, title })
  try {
    // 等全局 spinner 绘制后再截图，避免半渲染状态进入 canvas
    await sleep(300)

    const rowEls = Array.from(tableClone.children).filter((el) => el.classList.contains('ant-row-flex'))
    if (!rowEls.length) {
      throw new Error('score table is empty')
    }
    const blockEls = [titleEl, legendRow, ...rowEls]

    const hostRect = host.getBoundingClientRect()
    const scale = pickScale(hostRect.width, hostRect.height)
    const master = await html2Canvas(host, {
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      scale
    })

    const pdf = new JsPDF('l', 'pt', 'a4')
    const k = (PAGE_WIDTH - PAGE_MARGIN * 2) / hostRect.width // CSS px → pt 统一缩放系数
    const pageBottom = PAGE_HEIGHT - PAGE_MARGIN
    const maxSegPx = (pageBottom - PAGE_MARGIN) / k // 单页可容纳的最大块高（CSS px）
    let y = PAGE_MARGIN

    for (const el of blockEls) {
      const rect = rectInHost(host, el)
      if (rect.h <= 0) continue
      // 块内竖向分段：正常块一段；超过单页高度的块按页高硬切兜底
      const segHeights = []
      for (let offset = 0; offset < rect.h; offset += maxSegPx) {
        segHeights.push(Math.min(maxSegPx, rect.h - offset))
      }
      segHeights.forEach((segPx, index) => {
        if (segPx < 0.5) return // 浮点边界产生的近零尾段直接跳过，避免零高 canvas
        if (y + segPx * k > pageBottom && y > PAGE_MARGIN) {
          pdf.addPage()
          y = PAGE_MARGIN
        }
        const slice = document.createElement('canvas')
        slice.width = Math.round(rect.w * scale)
        slice.height = Math.round(segPx * scale)
        slice.getContext('2d').drawImage(
          master,
          rect.x * scale,
          (rect.y + index * maxSegPx) * scale,
          rect.w * scale,
          segPx * scale,
          0,
          0,
          slice.width,
          slice.height
        )
        pdf.addImage(slice.toDataURL('image/jpeg', 1.0), 'JPEG', PAGE_MARGIN, y, rect.w * k, segPx * k)
        y += segPx * k
      })
    }
    pdf.save(fileName)
  } finally {
    wrapper.remove()
  }
}
